import { useQuery } from "@tanstack/react-query";
import { AlertTriangle, Info } from "lucide-react";
import { useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../../app/AuthContext";
import { api } from "../../shared/api/client";
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  InlineAlert,
  StatusBadge,
  cn,
} from "../../shared/ui";
import type { PageResponse, ProjectDetail, WorkItemSummary } from "../../shared/types/api";
import { todayLocal } from "../meetings/meetingVocabulary";
import {
  REVIEW_WAIT_IS_LONG,
  needsReview,
  needsRetest,
  waitingForReviewDays,
} from "../workitems/workItemVocabulary";
import { HealthOverrideDrawer } from "./HealthOverrideDrawer";
import { StatusDrawer } from "./StatusDrawer";
import {
  CATEGORY_LABELS,
  CATEGORY_ORDER,
  HEALTH_TONE,
  STATUS_LABELS,
  formatHours,
  formatPercent,
  formatSlippage,
  formatVariance,
} from "./projectVocabulary";

/**
 * The project dashboard (§95).
 *
 * <p>Built around one comparison: what was committed against what is planned now. Everything else
 * on the page exists to explain that number.
 *
 * <p>Actual and remaining hours read zero until worklogs and work-item estimates arrive in later
 * phases. The panel says so rather than showing a confident zero, which would read as "nothing
 * spent" when it means "not measured yet".
 */
export function ProjectOverviewTab({
  project,
  onChanged,
}: {
  project: ProjectDetail;
  onChanged: () => void;
}) {
  const { can } = useAuth();
  const [changingStatus, setChangingStatus] = useState(false);
  const [overridingHealth, setOverridingHealth] = useState(false);

  const { forecast } = project;
  const over = forecast.effortVarianceHours > 0;

  /* Finished work with no reviewer yet. Asked for separately from the board because the whole
     point is that nobody is looking at these — they do not appear in anybody's queue. */
  const today = todayLocal();
  const waiting = useQuery({
    queryKey: ["work-items", project.id, "awaiting-review"],
    queryFn: () =>
      api.get<PageResponse<WorkItemSummary>>(
        `/api/v1/work-items?projectId=${project.id}&size=200`,
      ),
  });
  /* Anywhere, not one column: work sent to testing unreviewed is exactly what this is for. */
  const rows = waiting.data?.content ?? [];
  const awaitingReview = rows.filter(needsReview);
  /* The other half of the same question. These have been reviewed — that is why they are not in
     the count above — and the testing that passed them predates the approval, so as a statement
     that the work is finished they are worth no more than the unreviewed ones. */
  const awaitingRetest = rows.filter(needsRetest);
  const longestWait = awaitingReview.reduce((worst, item) => {
    const days = waitingForReviewDays(
      { status: "DEV_DONE", devCompletedDate: item.devCompletedDate }, today);
    return days != null && days > worst ? days : worst;
  }, 0);

  return (
    <>
      {awaitingRetest.length > 0 && (
        <div className="mb-4">
          <InlineAlert tone="warning">
            <Link
              to={`/projects/${project.id}?tab=work`}
              className="font-medium underline-offset-2 hover:underline"
            >
              {awaitingRetest.length} task{awaitingRetest.length === 1 ? "" : "s"} tested before
              the review that approved {awaitingRetest.length === 1 ? "it" : "them"}
            </Link>
            {" · each owes another round of testing against the code the reviewer accepted"}
          </InlineAlert>
        </div>
      )}

      {awaitingReview.length > 0 && (
        <div className="mb-4">
          <InlineAlert tone={longestWait >= REVIEW_WAIT_IS_LONG ? "warning" : "info"}>
            <Link
              to={`/projects/${project.id}?tab=work`}
              className="font-medium underline-offset-2 hover:underline"
            >
              {awaitingReview.length} task{awaitingReview.length === 1 ? "" : "s"} finished and
              not yet code reviewed
            </Link>
            {longestWait > 0 && ` · longest ${longestWait} day${longestWait === 1 ? "" : "s"}`}
          </InlineAlert>
        </div>
      )}

      <div className="grid gap-4 lg:grid-cols-3">
        <Card className="lg:col-span-2">
          <CardHeader
            title="Effort"
            subtitle="What was committed, against what is planned now."
          />
          <CardBody>
            {!forecast.hasBaseline ? (
              <InlineAlert tone="info">
                No baseline has been approved yet, so there is nothing to measure the plan
                against. Approve one on the Baselines tab to start tracking variance.
              </InlineAlert>
            ) : (
              <>
                <div className="grid grid-cols-2 gap-x-6 gap-y-4 sm:grid-cols-4">
                  <Figure label="Committed" value={formatHours(forecast.baselineHours)} />
                  <Figure label="Planned now" value={formatHours(project.approvedHours)} />
                  <Figure
                    label="Variance"
                    value={formatVariance(forecast.effortVarianceHours)}
                    tone={forecast.effortVarianceHours === 0 ? undefined : over ? "bad" : "good"}
                  />
                  <Figure
                    label="Variance %"
                    value={formatPercent(forecast.effortVariancePercent)}
                    tone={forecast.effortVariancePercent === 0 ? undefined : over ? "bad" : "good"}
                  />
                </div>

                {forecast.willExceedBaseline && (
                  <div className="mt-4 flex items-start gap-2.5 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5">
                    <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
                    <p className="text-sm text-amber-700">
                      The current plan exceeds what was approved by{" "}
                      <span className="tnum font-medium">
                        {formatHours(forecast.effortVarianceHours)}
                      </span>
                      . Revising the commitment means approving a new baseline version.
                    </p>
                  </div>
                )}
              </>
            )}
          </CardBody>
        </Card>

        <Card>
          <CardHeader
            title="Health"
            subtitle={project.healthOverride ? "Set manually" : "Derived from variance"}
            action={
              can("PROJECT_EDIT") && (
                <Button variant="ghost" size="sm" onClick={() => setOverridingHealth(true)}>
                  Change
                </Button>
              )
            }
          />
          <CardBody className="space-y-3">
            <div className="flex items-center gap-2">
              <StatusBadge tone={HEALTH_TONE[project.health]}>{project.health}</StatusBadge>
              {project.healthOverride && forecast.impliedHealth !== project.healthOverride && (
                <span className="text-xs text-ink-500">
                  variance suggests {forecast.impliedHealth}
                </span>
              )}
            </div>
            {project.healthOverrideReason && (
              <p className="text-sm text-ink-500">{project.healthOverrideReason}</p>
            )}
            <div className="border-t border-ink-100 pt-3">
              <p className="mb-1.5 text-sm text-ink-500">Status</p>
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-ink-900">
                  {STATUS_LABELS[project.status]}
                </span>
                {can("PROJECT_EDIT") && (
                  <Button variant="ghost" size="sm" onClick={() => setChangingStatus(true)}>
                    Change
                  </Button>
                )}
              </div>
            </div>
          </CardBody>
        </Card>

        <Card className="lg:col-span-2">
          <CardHeader
            title="Approved man-hours"
            subtitle={`${formatHours(project.approvedHours)} across ${
              Object.keys(project.budgetHours).length
            } categories`}
          />
          <CardBody className="p-0">
            <ul>
              {CATEGORY_ORDER.filter((category) => project.budgetHours[category]).map(
                (category) => {
                  const hours = project.budgetHours[category] ?? 0;
                  const share =
                    project.approvedHours === 0 ? 0 : (hours / project.approvedHours) * 100;
                  return (
                    <li
                      key={category}
                      className="flex items-center gap-3 border-b border-ink-100 px-5 py-2.5 last:border-0"
                    >
                      <span className="w-40 shrink-0 text-sm text-ink-700">
                        {CATEGORY_LABELS[category]}
                      </span>
                      {/* A bar rather than a pie: comparing lengths on a shared baseline is far
                          easier than comparing angles, and these are shares of one total. */}
                      <span className="h-1.5 flex-1 overflow-hidden rounded-full bg-ink-100">
                        <span
                          className="block h-full rounded-full bg-brand-500"
                          style={{ width: `${share}%` }}
                        />
                      </span>
                      <span className="tnum w-20 shrink-0 text-right text-sm font-medium text-ink-900">
                        {formatHours(hours)}
                      </span>
                    </li>
                  );
                },
              )}
            </ul>
          </CardBody>
        </Card>

        <Card>
          <CardHeader title="Schedule" subtitle="Committed against forecast." />
          <CardBody className="space-y-3">
            <Row label="Planned start" value={project.plannedStartDate} />
            <Row
              label="Committed finish"
              value={forecast.baselineFinish ?? "no baseline"}
              muted={!forecast.baselineFinish}
            />
            <Row label="Forecast finish" value={forecast.forecastFinish ?? "—"} />
            <div className="border-t border-ink-100 pt-3">
              <Row
                label="Slippage"
                value={
                  forecast.hasBaseline
                    ? formatSlippage(forecast.scheduleVarianceDays)
                    : "not measurable"
                }
                tone={
                  !forecast.hasBaseline
                    ? undefined
                    : forecast.scheduleVarianceDays > 0
                      ? "bad"
                      : "good"
                }
              />
            </div>
          </CardBody>
        </Card>
      </div>

      <div className="mt-4 flex items-start gap-3 rounded-xl border border-brand-200 bg-brand-50 px-5 py-4">
        <Info className="mt-0.5 h-5 w-5 shrink-0 text-brand-600" />
        <p className="text-sm text-brand-700/90">
          Actual hours come from time logged on timesheets, and remaining from the estimates on
          open work items. A zero here means nothing has been recorded against this project, not
          that nothing was spent.
        </p>
      </div>

      <StatusDrawer
        project={changingStatus ? project : null}
        onClose={() => setChangingStatus(false)}
        onSaved={onChanged}
      />
      <HealthOverrideDrawer
        project={overridingHealth ? project : null}
        onClose={() => setOverridingHealth(false)}
        onSaved={onChanged}
      />
    </>
  );
}

function Figure({
  label,
  value,
  tone,
}: {
  label: string;
  value: string;
  tone?: "good" | "bad";
}) {
  return (
    <div>
      <p className="text-sm text-ink-500">{label}</p>
      <p
        className={cn(
          "tnum font-display mt-0.5 text-xl font-semibold",
          tone === "bad" ? "text-red-600" : tone === "good" ? "text-emerald-600" : "text-ink-900",
        )}
      >
        {value}
      </p>
    </div>
  );
}

function Row({
  label,
  value,
  muted,
  tone,
}: {
  label: string;
  value: string;
  muted?: boolean;
  tone?: "good" | "bad";
}) {
  return (
    <div className="flex items-center justify-between text-sm">
      <span className="text-ink-500">{label}</span>
      <span
        className={cn(
          "tnum",
          muted
            ? "text-ink-400"
            : tone === "bad"
              ? "font-medium text-red-600"
              : tone === "good"
                ? "text-emerald-600"
                : "text-ink-900",
        )}
      >
        {value}
      </span>
    </div>
  );
}
